/**
 * Subscription purchase boundary.
 *
 * Pro is sold exclusively through native In-App Purchases (Apple App Store
 * via StoreKit, Google Play via Play Billing), mediated by RevenueCat. There
 * is no web checkout. On web builds the purchase button is hidden by the
 * PaywallModal; this helper is kept as a thin shim that surfaces a clear
 * "use the app" message if a caller ever reaches it.
 *
 * On native, the actual RevenueCat purchase flow runs via the SessionProvider
 * `purchasePro` hook (see PaywallModal). This module's startSubscribe is
 * effectively a no-op kept for legacy call sites and tests.
 */
import { getPlatform, isNative } from "./platform";

export type PlanCode = "pro_monthly" | "pro_yearly";

export interface SubscribeResult {
  /** Set when the purchase completed entirely client-side (native path). */
  purchased?: boolean;
}

interface SubscribeDeps {
  // Kept for backwards compatibility with existing call sites. Unused now
  // that there is no server-side checkout endpoint.
  authedFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

export async function startSubscribe(
  _plan: PlanCode,
  _deps: SubscribeDeps,
): Promise<SubscribeResult> {
  if (isNative()) {
    // The real native purchase runs through RevenueCat in
    // SessionProvider.purchasePro(); this helper is unreachable on the
    // happy path. Throw loudly so we notice if the wiring regresses.
    throw new Error(
      "In-app purchases are handled by the RevenueCat flow; this code path should not be reached on native.",
    );
  }
  // Web has no purchase flow: Apple / Google rules require StoreKit / Play
  // Billing for digital subscriptions, and there is no web fallback. The
  // PaywallModal hides the purchase CTA on web, but this throw is the
  // second line of defence.
  const platform = getPlatform();
  throw new Error(
    `In-app subscriptions are available in the iOS and Android apps. The ${platform} build cannot start a purchase.`,
  );
}
